import { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Send,
  Sparkles,
  RotateCcw,
  Settings,
  Loader2,
} from 'lucide-react'
import TerminalAIMessage from './TerminalAIMessage'
import TerminalAICommandCard from './TerminalAICommandCard'
import TerminalAISettings from './TerminalAISettings'
import TerminalAISuModal from './TerminalAISuModal'
import TerminalAIExecutionPlan from './TerminalAIExecutionPlan'

export default function TerminalAIPanel({
  isOpen,
  onClose,
  messages,
  loading,
  error,
  settings,
  onSettingsChange,
  tokenStats,
  onSend,
  onRunCommand,
  onNewConversation,
  // Su escalation
  suModal,
  confirmSu,
  cancelSu,
  isLinuxProfile,
  // Execution plan
  executionPlan,
  approveExecutionPlan,
  rejectExecutionPlan,
  // Lite agent
  onRetryAsLite,
}) {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200)
    }
  }, [isOpen])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!input.trim() || loading) return
    onSend(input.trim())
    setInput('')
  }

  if (!isOpen) return null

  const isPro = settings?.proMode
  const totalTokens = (tokenStats?.input || 0) + (tokenStats?.output || 0)

  return (
    <div className="absolute top-0 right-0 bottom-0 z-40 flex w-[420px] max-w-full flex-col border-l border-slate-700/60 bg-slate-900/95 backdrop-blur-sm shadow-2xl">

      {/* Modal de escalada su — renderizado sobre o painel */}
      {suModal && (
        <TerminalAISuModal
          mode={suModal.mode}
          command={suModal.command}
          onConfirm={confirmSu}
          onCancel={cancelSu}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-700/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className={`h-4 w-4 ${isPro ? 'text-theme-secondary' : 'text-theme-primary'}`} />
          <h3 className="text-sm font-semibold text-slate-200">{t('terminalAi.title')}</h3>
          {isPro ? (
            <span className="inline-flex items-center rounded-full bg-theme-secondary/30 border border-theme-secondary/50 px-2 py-0.5 text-[10px] font-semibold text-theme-secondary">
              PRO ✦
            </span>
          ) : settings?.liteMode ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/50 border border-amber-700/40 px-2 py-0.5 text-[10px] font-medium text-amber-400">
              {t('terminalAi.lite')} ⚡
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-700/60 border border-slate-600/50 px-2 py-0.5 text-[10px] font-medium text-slate-400">
              {t('terminalAi.normal')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewConversation}
            title={t('terminalAi.newConversation')}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-700/60 hover:text-slate-200"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowSettings((v) => !v)}
            title={t('terminalAi.settings')}
            className={`rounded p-1.5 transition ${
              showSettings
                ? 'bg-slate-700 text-theme-primary'
                : 'text-slate-400 hover:bg-slate-700/60 hover:text-slate-200'
            }`}
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            title={t('common.buttons.close')}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-700/60 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Settings */}
      {showSettings && (
        <div className="border-b border-slate-700/60 px-4 py-3">
          <TerminalAISettings
            settings={settings}
            onChange={onSettingsChange}
            onClose={() => setShowSettings(false)}
            tokenStats={tokenStats}
          />
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3 terminal-scroll">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3 text-slate-500">
            <Sparkles className={`h-8 w-8 ${isPro ? 'text-theme-secondary' : 'text-slate-600'}`} />
            <p className="text-sm">
              {isPro ? t('terminalAi.introPro') : t('terminalAi.intro')}
            </p>
            <p className="text-xs text-slate-600">
              {t('terminalAi.exampleQuestion')}
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i}>
            <TerminalAIMessage message={msg} onRetryAsLite={msg.suggestLiteAgent ? onRetryAsLite : undefined} />
            {msg.commands?.length > 0 && (
              <div className="mt-2 ml-9 space-y-2">
                {msg.commands.map((cmd, j) => (
                  <TerminalAICommandCard
                    key={j}
                    command={cmd}
                    onExecute={onRunCommand}
                    disabled={loading}
                    settings={settings}
                    onSettingsChange={onSettingsChange}
                    isLinuxProfile={isLinuxProfile}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 px-2 py-1.5">
            <Loader2 className={`h-4 w-4 animate-spin ${isPro ? 'text-theme-secondary' : 'text-theme-primary'}`} />
            <span className="text-xs text-slate-400 animate-pulse">
              {isPro ? t('terminalAi.investigatingPro') : t('terminalAi.investigating')}
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Planner de execução — aparece quando o agente planeja múltiplos comandos */}
      {executionPlan && (
        <TerminalAIExecutionPlan
          plan={executionPlan}
          onApprove={approveExecutionPlan}
          onCancel={rejectExecutionPlan}
        />
      )}

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="border-t border-slate-700/60 px-4 py-3"
      >
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={executionPlan ? t('terminalAi.waitingPlanApproval') : t('terminalAi.inputPlaceholder')}
            disabled={loading || !!executionPlan}
            className="flex-1 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-theme-primary focus:outline-none disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={loading || !input.trim() || !!executionPlan}
            className={`flex items-center justify-center rounded-lg px-3 py-2 text-white transition disabled:opacity-40 disabled:cursor-not-allowed ${
              isPro ? 'bg-theme-secondary hover:bg-theme-secondary/90' : 'bg-theme-primary hover:bg-theme-primary/90'
            }`}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>

      {/* Rodapé discreto de tokens */}
      {totalTokens > 0 && (
        <div className="border-t border-slate-800/60 px-4 py-1 flex items-center justify-end gap-3">
          <span className="text-[10px] font-mono text-slate-600">
            ↑{(tokenStats?.input || 0).toLocaleString()}
          </span>
          <span className="text-[10px] font-mono text-slate-600">
            ↓{(tokenStats?.output || 0).toLocaleString()}
          </span>
          <span
            className={`text-[10px] font-mono font-medium ${
              totalTokens > 50000
                ? 'text-red-500/70'
                : totalTokens > 20000
                  ? 'text-amber-500/70'
                  : 'text-slate-500'
            }`}
          >
            {totalTokens.toLocaleString()} tkns
          </span>
        </div>
      )}
    </div>
  )
}
